import { Buffer } from 'buffer'

import { resizeCanvas } from './canvas.utils'

const SizeType = {
  Absolute: 0, // exact size
  Fraction: 1, // size as part of parent
  Relative: 2 // size together with siblings as part of parent
}

class ImageResizer {
  constructor() {}

  resize(
    targetWidth: number,
    targetHeight: number,
    width: number,
    height: number,
    data: number[]
  ): number[] {
    const result: number[] = []

    const dx = width / targetWidth
    const dy = height / targetHeight

    for (let y = 0; y < targetHeight; y++) {
      for (let x = 0; x < targetWidth; x++) {
        const iy = Math.floor(dy * y)
        const ix = Math.floor(dx * x)

        const value = data[iy * width + ix]

        result.push(value ? 1 : 0)
      }
    }

    return result
  }
}

const ImageResizerInstance = new ImageResizer()

class Size {
  public value: number
  public sizeType: number

  constructor(value?: number, sizeType?: number) {
    this.value = value || 0
    this.sizeType = sizeType || SizeType.Absolute
  }

  getValue(unitSize?: number): number {
    if (typeof unitSize == 'number' && this.sizeType == SizeType.Relative) {
      return this.value * unitSize
    }
    return this.value
  }

  toString(): string {
    return this.value + (this.sizeType == SizeType.Relative ? '*' : '')
  }
}

class Spacing {
  public left: number
  public top: number
  public right: number
  public bottom: number

  constructor(left?: number, top?: number, right?: number, bottom?: number) {
    this.left = left || 0
    this.top = top == undefined ? this.left : top
    this.right = right == undefined ? this.left : right
    this.bottom = bottom == undefined ? this.top : bottom
  }

  get horizontal(): number {
    return this.left + this.right
  }

  get vertical(): number {
    return this.top + this.bottom
  }

  get horizontalDifference(): number {
    return Math.abs(this.left - this.top)
  }

  get verticalDifference(): number {
    return Math.abs(this.top - this.bottom)
  }

  toString(): string {
    return this.left + ', ' + this.top + ', ' + this.right + ', ' + this.bottom
  }
}

class GridPosition {
  public column: number
  public row: number

  constructor(column?: number, row?: number) {
    this.column = column || 0
    this.row = row || 0
  }

  toString(): string {
    return this.column + ', ' + this.row
  }
}

class PrintDensity {
  public value: number

  constructor(value: number) {
    this.value = value
  }

  toString(): string {
    return this.value + ' dpmm'
  }
}

class GraphicData {
  public data: number[]
  public width: number
  public height: number

  constructor(width?: number, height?: number, data?: number[]) {
    this.data = data || []
    this.width = width || 0
    this.height = height || 0
  }

  toString(): string {
    return this.width + ' x ' + this.height
  }
}

class BaseElement {
  public invert: boolean
  public fixed: boolean
  public grid: GridPosition

  constructor() {
    this.invert = false
    this.fixed = false
    this.grid = new GridPosition()
  }

  generateZPL(
    _offsetLeft: number,
    _offsetTop: number,
    _availableWidth: number,
    _availableHeight: number,
    _widthUnits: number,
    _heightUnits: number
  ): string {
    return ''
  }
}

class BaseVisualElement extends BaseElement {
  public width: Size
  public height: Size
  public top: Size
  public left: Size
  public margin: Spacing
  public border?: number
  public content: any[]

  constructor() {
    super()
    this.width = new Size()
    this.height = new Size()
    this.top = new Size()
    this.left = new Size()
    this.margin = new Spacing()
    this.content = []
  }

  getPosition(
    offsetLeft: number,
    offsetTop: number,
    availableWidth: number,
    availableHeight: number,
    widthUnits: number,
    heightUnits: number
  ): { left: number; top: number; width: number; height: number } {
    let left: number = this.getSize(this.left, widthUnits) + this.margin.left
    let top: number = this.getSize(this.top, heightUnits) + this.margin.top

    const width = this.getSize(this.width, widthUnits) || availableWidth - this.margin.horizontal
    const height = this.getSize(this.height, heightUnits) || availableHeight - this.margin.vertical

    if (typeof this.top == 'object' && this.top.sizeType == SizeType.Fraction) {
      top = availableHeight * this.top.value
    }
    if (typeof this.left == 'object' && this.left.sizeType == SizeType.Fraction) {
      left = availableWidth * this.left.value
    }

    return {
      left: Math.round(left + offsetLeft),
      top: Math.round(top + offsetTop),
      width: Math.round(width),
      height: Math.round(height)
    }
  }

  getSize(prop: Size | number, unitSize?: number): number {
    if (typeof prop == 'number') {
      return prop
    } else {
      return prop.getValue(unitSize)
    }
  }

  calculateUnits(): {
    absolute: { width: number; height: number }
    relative: { width: number; height: number }
  } {
    const units = {
      absolute: {
        width: 0,
        height: 0
      },
      relative: {
        width: 0,
        height: 0
      }
    }

    const elements: any[] = this.content || []

    for (const e_id in elements) {
      const element = elements[e_id]

      units.absolute.width += element.margin.horizontal + (this.border || 0)
      units.absolute.height += element.margin.vertical + (this.border || 0)

      if (typeof element.border == 'number') {
        units.absolute.width += element.border * 2
        units.absolute.height += element.border * 2
      }

      if (typeof element.width == 'number') {
        units.absolute.width += element.width
      } else if (element.width.sizeType == SizeType.Absolute) {
        units.absolute.width += element.width.value
      } else {
        units.relative.width += element.width.value
      }

      if (typeof element.height == 'number') {
        units.absolute.height += element.height
      } else if (element.height.sizeType == SizeType.Absolute) {
        units.absolute.height += element.height.value
      } else {
        units.relative.height += element.height.value
      }
    }

    return units
  }
}

class BaseContainerElement extends BaseVisualElement {
  public padding: Spacing
  public content: any[]

  constructor() {
    super()
    this.padding = new Spacing()
    this.content = []
  }

  calculateSizing(
    availableWidth: number,
    availableHeight: number,
    _widthUnits?: number,
    _heightUnits?: number
  ): {
    spacingTop: number
    spacingLeft: number
    width: number
    height: number
    widthUnits: number
    heightUnits: number
  } {
    const units = this.calculateUnits()

    const spacingLeft = this.margin.left + this.padding.left
    const spacingTop = this.margin.top + this.padding.top

    const spacingHorizontal = spacingLeft + this.margin.right + this.padding.right
    const spacingVertical = spacingTop + this.margin.bottom + this.padding.right

    const width = availableWidth - spacingHorizontal - (this.border || 0) * 2
    const height = availableHeight - spacingVertical - (this.border || 0) * 2

    const widthUnits = (width - units.absolute.width) / (units.relative.width || 1)
    const heightUnits = (height - units.absolute.height) / (units.relative.height || 1)

    return {
      spacingTop: spacingTop,
      spacingLeft: spacingLeft,
      width: width,
      height: height,
      widthUnits: widthUnits,
      heightUnits: heightUnits
    }
  }

  generateZPL(
    offsetLeft: number,
    offsetTop: number,
    availableWidth: number,
    availableHeight: number,
    widthUnits?: number,
    heightUnits?: number
  ): string {
    const sizing = this.calculateSizing(availableWidth, availableHeight, widthUnits, heightUnits)

    let zpl = ''

    for (const c_id in this.content) {
      const element = this.content[c_id]

      let left = offsetLeft + sizing.spacingLeft + (this.border || 0)
      let top = offsetTop + sizing.spacingTop + (this.border || 0)

      if (element.fixed) {
        left = this.getSize(element.left)
        top = this.getSize(element.top)
      }

      zpl += element.generateZPL(
        left,
        top,
        sizing.width,
        sizing.height,
        sizing.widthUnits,
        sizing.heightUnits
      )
    }

    return zpl
  }
}

class Label extends BaseContainerElement {
  public printDensity: PrintDensity

  constructor() {
    super()
    this.printDensity = new PrintDensity(8)
  }

  generateZPL(): string {
    let zpl = '^XA'

    zpl += '\n'

    const width = this.getSize(this.width) * this.printDensity.value
    const height = this.getSize(this.height) * this.printDensity.value

    zpl += super.generateZPL(0, 0, width, height)

    zpl += '^XZ'

    return zpl
  }
}

class BaseGraphic extends BaseContainerElement {
  public border: number
  public fill: boolean

  constructor() {
    super()
    this.border = 0
    this.fill = false
  }
}

class Box extends BaseGraphic {
  protected cornerRadius: number

  constructor() {
    super()
    this.cornerRadius = 0
  }

  generateZPL(
    offsetLeft: number,
    offsetTop: number,
    availableWidth: number,
    availableHeight: number,
    widthUnits: number,
    heightUnits: number
  ): string {
    const position = this.getPosition(
      offsetLeft,
      offsetTop,
      availableWidth,
      availableHeight,
      widthUnits,
      heightUnits
    )

    let zpl = '^FO' + Math.round(position.left) + ',' + Math.round(position.top)

    if (this.invert) {
      zpl += '^FR'
    }

    let thickness = this.border

    if (this.fill) {
      thickness = Math.min(position.width, position.height)
    }

    const shorterSide = Math.min(position.width, position.height)
    const roundingIndex = Math.round((this.cornerRadius * 16) / shorterSide)

    if (thickness > 0) {
      zpl +=
        '^GB' +
        position.width +
        ',' +
        position.height +
        ',' +
        (thickness || '') +
        ',,' +
        roundingIndex +
        '^FS' +
        '\n'
    } else {
      zpl += '\n'
    }

    zpl += super.generateZPL(position.left, position.top, position.width, position.height)

    return zpl
  }
}

class Graphic extends BaseVisualElement {
  public data: GraphicData
  public border: number

  constructor() {
    super()
    this.data = new GraphicData()
    this.border = 0
  }

  generateContainer(): Box {
    const container = new Box()
    container.border = this.border
    container.margin = this.margin
    container.top = this.top
    container.left = this.left
    return container
  }

  generateZPL(
    offsetLeft: number,
    offsetTop: number,
    availableWidth: number,
    availableHeight: number,
    widthUnits: number,
    heightUnits: number
  ): string {
    const container = this.generateContainer()
    let zpl = container.generateZPL(
      offsetLeft,
      offsetTop,
      availableWidth,
      availableHeight,
      widthUnits,
      heightUnits
    )

    const position = this.getPosition(
      offsetLeft,
      offsetTop,
      availableWidth,
      availableHeight,
      widthUnits,
      heightUnits
    )
    const imageData = ImageResizerInstance.resize(
      position.width,
      position.height,
      this.data.width,
      this.data.height,
      this.data.data
    )

    zpl += '^FO' + Math.round(position.left) + ',' + Math.round(position.top)

    if (this.invert) {
      zpl += '^FR'
    }

    const widthBytes = Math.ceil(position.width / 8)
    const byteCount = widthBytes * position.height
    let hexData = generateHexAscii(position.width, position.height, imageData)
    hexData = encodeHexAscii(hexData)

    zpl += '^GFA,' + byteCount + ',' + byteCount + ',' + widthBytes + ',' + hexData + '^FS'

    return zpl
  }
}

function generateHexAscii(width: number, height: number, imageData: number[]) {
  let index = 0
  let bitIndex = 0
  let output = ''

  let currentValue = 0

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const value = imageData[index]
      currentValue += value << (7 - bitIndex)

      if (bitIndex == 7 || x == width - 1) {
        let valueString = currentValue.toString(16).toUpperCase()
        if (currentValue < 16) {
          valueString = '0' + valueString
        }
        output += valueString
        currentValue = 0
        bitIndex = -1
      }

      index++
      bitIndex++
    }
    output += '\n'
  }
  return output
}

function encodeHexAscii(data: string) {
  const mapCode = {
    1: 'G',
    2: 'H',
    3: 'I',
    4: 'J',
    5: 'K',
    6: 'L',
    7: 'M',
    8: 'N',
    9: 'O',
    10: 'P',
    11: 'Q',
    12: 'R',
    13: 'S',
    14: 'T',
    15: 'U',
    16: 'V',
    17: 'W',
    18: 'X',
    19: 'Y',
    20: 'g',
    40: 'h',
    60: 'i',
    80: 'j',
    100: 'k',
    120: 'l',
    140: 'm',
    160: 'n',
    180: 'o',
    200: 'p',
    220: 'q',
    240: 'r',
    260: 's',
    280: 't',
    300: 'u',
    320: 'v',
    340: 'w',
    360: 'x',
    380: 'y',
    400: 'z'
  } as const

  let outputCode = ''
  let currentLine = ''
  let previousLine = ''

  let newSection = true
  let currentChar = undefined
  let counter = 1

  for (let i = 0; i < data.length; i++) {
    if (newSection) {
      currentChar = data[i]
      counter = 1
      newSection = false
      continue
    }

    if (data[i] == '\n') {
      if (currentChar == '0') {
        currentLine += ','
      } else if (currentChar == 'F') {
        currentLine += '!'
      } else if (counter > 20) {
        const value = (Math.floor(counter / 20) * 20) as keyof typeof mapCode
        currentLine += mapCode[value]

        const counterMod = (counter % 20) as keyof typeof mapCode & 0
        if (counterMod != 0) {
          currentLine += mapCode[counterMod]
        }

        currentLine += currentChar
      }

      newSection = true
      if (currentLine == previousLine) {
        outputCode += ':'
      } else {
        outputCode += currentLine
        previousLine = currentLine
      }
      currentLine = ''
      continue
    }

    if (currentChar == data[i]) {
      counter++
    } else {
      if (counter > 20) {
        const value = (Math.floor(counter / 20) * 20) as keyof typeof mapCode
        currentLine += mapCode[value]

        const counterMod = (counter % 20) as keyof typeof mapCode & 0
        if (counterMod != 0) {
          currentLine += mapCode[counterMod]
        }
      } else {
        currentLine += mapCode[counter as keyof typeof mapCode] || counter
      }
      currentLine += currentChar
      currentChar = data[i]
      counter = 1
    }
  }
  return outputCode
}

export class ZPLEncoder {
  private instance: Label | null = null

  constructor() {}

  image(
    canvas: HTMLCanvasElement,
    { width, height, paddingLeft }: { width: number; height: number; paddingLeft: number }
  ) {
    const graphic = new Graphic()
    const label = new Label()
    label.width = new Size(width)
    label.height = new Size(height)

    const context = resizeCanvas(canvas, width, height)
    const imageData = context.getImageData(0, 0, width, height)

    let index = 0
    const imageBits = []

    for (let y = 0; y < imageData.height; y++) {
      for (let x = 0; x < imageData.width; x++) {
        const red = imageData.data[index++]
        const green = imageData.data[index++]
        const blue = imageData.data[index++]
        const opacity = imageData.data[index++]

        let value = 0
        if (opacity != 0) {
          value = (red + green + blue) / 3 < 180 ? 1 : 0
        }

        imageBits.push(value)
      }
    }

    graphic.width = new Size(width)
    graphic.height = new Size(height)
    graphic.data = new GraphicData(width, height, imageBits)

    label.padding = new Spacing(paddingLeft, 0, 0, 0)
    label.content.push(graphic)

    this.instance = label

    return this
  }

  encode() {
    if (!this.instance) {
      throw new Error('No image has been added')
    }

    return Buffer.from(this.instance.generateZPL())
  }
}
